import { booleanAttribute, computed, Directive, input, OnInit, TrackByFunction } from '@angular/core';
import { InputFieldBaseComponent } from './input-field-base.component';
import { findOption, getRealOptionLabel, IOption } from '../input.types';
import { SafeHtml } from '@angular/platform-browser';
import { isArray } from '@mng/mng-ts-helpers';

export type labelProvider = (item: object, data?: any) => string;

export interface ISelectionData {
  dataArray: any[];
  valueProperty?: string;
  labelProperty: string | labelProvider;
  optionalDataProperty?: any;
}

@Directive()
export abstract class InputFieldSelectionBaseComponent extends InputFieldBaseComponent implements OnInit {
  readonly internalOptions = input<IOption[]>(undefined, { alias: 'options' });

  readonly selectFirstIfEmpty = input(false, { transform: booleanAttribute });
  readonly withEmptySelection = input(false, { transform: booleanAttribute });

  readonly selectionData = input<ISelectionData>();

  readonly trackByKey = input<keyof IOption>('name');
  trackBy: TrackByFunction<IOption> = (_: number, item: IOption): any => item[this.trackByKey()];

  readonly optionsFromSelectionData = computed<IOption[] | undefined>(() => {
    if (this.selectionData()) {
      const optionData = this.selectionData();
      let result = optionData.dataArray.map((item: object) => ({
        name:
          typeof optionData.labelProperty === 'string'
            ? item[optionData.labelProperty]
            : optionData.labelProperty(item, optionData.optionalDataProperty),
        value: optionData.valueProperty ? item[optionData.valueProperty] : item
      }));
      if (this.withEmptySelection()) {
        result = [{ value: null, name: '-' }, ...result];
      }
      return result;
    } else {
      return undefined;
    }
  });

  readonly options = computed<IOption[]>(
    () => this.field().options || this.internalOptions() || this.optionsFromSelectionData() || []
  );

  getRealOptionLabel(option: IOption, preferHtmlInfo = true): SafeHtml | string {
    if (preferHtmlInfo && option.htmlInfo) {
      return option.htmlInfo;
    } else {
      return getRealOptionLabel(option);
    }
  }

  getTooltip(option: IOption) {
    if (option.tooltip) {
      return typeof option.tooltip === 'string' ? option.tooltip : option.tooltip();
    } else {
      return undefined;
    }
  }

  getSelectedLabel(): SafeHtml | string {
    if (this.field().control.value && this.options()) {
      const labelProvider = (value, preferHtmlInfo = true) => {
        const option = findOption(this.options(), value);
        if (option) {
          const result = this.getRealOptionLabel(option, preferHtmlInfo);
          return result;
        } else {
          console.error(`Option für den value ${value} wurde nicht gefunden`);
          return value;
        }
      };

      const value = this.field().control.value;
      return isArray(value)
        ? value.map((itemValue) => labelProvider(itemValue, false)).join(', ')
        : labelProvider(value);
    } else {
      return '';
    }
  }

  ngOnInit() {
    if (this.selectFirstIfEmpty() && !this.field().control.value && this.options() && this.options().length > 0) {
      this.field().control.setValue(this.options()[0].value);
    }
  }
}
